/* import __COLOCATED_TEMPLATE__ from './store-content.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type ShopifyIntegration from 'embercom/models/shopify/integration';
import type NotificationsService from 'embercom/services/notifications-service';

interface StoreContentSignature {
  Args: {};
}

export default class StoreContent extends Component<StoreContentSignature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked shops: ShopifyIntegration[] = [];

  constructor(owner: unknown, args: StoreContentSignature['Args']) {
    super(owner, args);
    taskFor(this.loadShops).perform();
    this.handlePostAuthNotification();
  }

  get isLoadingShops() {
    return taskFor(this.loadShops).isRunning;
  }

  get hasShops() {
    return this.shops.length > 0;
  }

  get postAuthRedirect() {
    return this.router.urlFor(
      'apps.app.automation.fin-ai-agent.ecommerce.store',
      this.appService.app.id,
    );
  }

  private handlePostAuthNotification() {
    let queryParams = this.router.currentRoute?.queryParams || {};
    let notifyKey: Record<string, string> = {
      shop_already_connected:
        'appstore.settings.shopify.connect-shopify.shop-already-connected-message',
      shop_reconnected: 'appstore.settings.shopify.connect-shopify.shop-reconnected',
    };
    let notifyMessage = queryParams.notify ? notifyKey[queryParams.notify] : null;
    if (notifyMessage) {
      this.notificationsService.notifyConfirmation(this.intl.t(notifyMessage));
      let currentRouteName = this.router.currentRouteName;
      let newQueryParams = { ...queryParams };
      delete newQueryParams.notify;
      this.router.replaceWith(currentRouteName, { queryParams: newQueryParams });
    }
  }

  @dropTask
  *loadShops(): TaskGenerator<void> {
    try {
      this.shops = yield this.store.findAll('shopify/integration');
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('appstore.settings.shopify.connect-shopify.retrieve-shops-failure'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Store::StoreContent': typeof StoreContent;
    'fin-ai-agent/store/store-content': typeof StoreContent;
  }
}
